export const TRAITS = [
  'bounded',
  'gentle',
  'named',
  'cyclic',
  'timed',
  'remembering',
  'reciprocal',
  'reflective',
  'released',
  'forceful',
  'endless',
  'hungry',
  'forgetful',
] as const

export type Trait = (typeof TRAITS)[number]

export const TRAIT_LABELS: Readonly<Record<Trait, string>> = {
  bounded: '경계',
  gentle: '감각',
  named: '명명',
  cyclic: '순환',
  timed: '시간',
  remembering: '기억',
  reciprocal: '호응',
  reflective: '반영',
  released: '해방',
  forceful: '강제',
  endless: '무한',
  hungry: '허기',
  forgetful: '망각',
}

export interface MetaphorChip {
  readonly id: string
  readonly text: string
  readonly resonance: string
}

export interface MetaphorSlot {
  readonly id: string
  readonly label: string
  readonly prompt: string
  readonly chips: readonly [MetaphorChip, MetaphorChip, MetaphorChip]
}

export type TraitInheritance = 'none' | 'all-previous-success-traits'

export interface AnomalyScenario {
  readonly id: string
  readonly number: 1 | 2 | 3
  readonly kind: 'thread' | 'bell' | 'mirror'
  readonly name: string
  readonly code: string
  readonly brief: string
  readonly question: string
  readonly sentenceLead: string
  readonly slots: readonly [MetaphorSlot, MetaphorSlot, MetaphorSlot]
  readonly requiredTraits: readonly Trait[]
  readonly forbiddenTraits: readonly Trait[]
  readonly inheritance: TraitInheritance
  readonly stableCopy: string
  readonly unstableCopy: string
}

export type MetaphorSelection = Readonly<Record<string, string>>

export interface SlotEvaluation {
  readonly slotId: string
  readonly chipId: string | null
  readonly valid: boolean
}

export interface MetaphorEvaluation {
  readonly scenarioId: string
  readonly sentence: string
  readonly selection: MetaphorSelection
  readonly slots: readonly SlotEvaluation[]
  readonly complete: boolean
  readonly stable: boolean
  readonly interpretedTraits: readonly Trait[]
  readonly inheritedTraits: readonly Trait[]
  readonly effectiveTraits: readonly Trait[]
  readonly requiredTraits: readonly Trait[]
  readonly forbiddenTraits: readonly Trait[]
  readonly missingRequiredTraits: readonly Trait[]
  readonly triggeredForbiddenTraits: readonly Trait[]
}

export interface StabilizationRecord {
  readonly scenarioId: string
  readonly roundIndex: number
  readonly selection: MetaphorSelection
  readonly sentence: string
  readonly interpretedTraits: readonly Trait[]
  readonly inheritedTraits: readonly Trait[]
  readonly effectiveTraits: readonly Trait[]
}

export interface AttemptRecord {
  readonly attemptIndex: number
  readonly roundIndex: number
  readonly evaluation: MetaphorEvaluation
}

export type GameStatus = 'playing' | 'won' | 'lost'

export interface GameState {
  readonly version: 1
  readonly status: GameStatus
  readonly roundIndex: number
  readonly integrity: number
  readonly selection: MetaphorSelection
  readonly lastEvaluation: MetaphorEvaluation | null
  readonly attempts: readonly AttemptRecord[]
  readonly successes: readonly StabilizationRecord[]
}

export type GameAction =
  | Readonly<{ type: 'SELECT_CHIP'; slotId: string; chipId: string }>
  | Readonly<{ type: 'CLEAR_SLOT'; slotId: string }>
  | Readonly<{
      type: 'RESOLVE_INTERPRETATION'
      traits: readonly Trait[]
      metaphor: string
    }>
